import { arrayToObject } from "utils/object-util";
import { TenantNameResolver } from "features/saasManagement/TenantNameResolver/TenantNameResolver";

export const loadStatusReasonOwner = {
    TENANT: 1,
    CLIENT: 2,
    OTHER: 3
};

export const loadStatusReasonOwnerObj = {
    [loadStatusReasonOwner.TENANT]: {
        name: <TenantNameResolver />,
        key: '1',
        color: '#33A6FE'
    },
    [loadStatusReasonOwner.CLIENT]: {
        name: 'Client',
        key: '2',
        color: '#ffc61b'
    },
    [loadStatusReasonOwner.OTHER]: {
        name: 'Other',
        key: '3',
        color: '#99D3FF'
    },

};

export const dateFilterTypes = {
    DAY: 1,
    WEEK: 2,
    MONTH: 3,
    QUARTER: 4,
    YEAR: 5,
    CUSTOM: 6
};

export const dateFilterTypesList = [
    { id: dateFilterTypes.DAY, name: 'Day' },
    { id: dateFilterTypes.WEEK, name: 'Week' },
    { id: dateFilterTypes.MONTH, name: 'Month' },
    { id: dateFilterTypes.QUARTER, name: 'Quarter' },
    { id: dateFilterTypes.YEAR, name: 'Year' }
];

export const dateFilterTypesListWithCustom = [
    ...dateFilterTypesList,
    { id: dateFilterTypes.CUSTOM, name: 'Custom Date Range' }
];

export const getDateFilterRanges = ({
    dayCount = 7,
    weekCount = 12,
    monthCount = 6,
    quarterCount = 5,
    yearCount = 2
} = {}) => ({
    [dateFilterTypes.DAY]: Array.from({ length: dayCount }, (_, i) => {
        let d = new Date();
        d.setDate(d.getDate() - i);
        d.setHours(0, 0, 0, 0); // Set hours to 00:00

        let end = new Date(d);
        end.setHours(23, 59, 59, 999); // Set end hours to 23:59

        return {
            id: i + 1, // Assign a unique ID (1 to dayCount)
            name: d.toLocaleDateString(), // Format as YYYY-MM-DD
            startDate: d,
            endDate: end
        };
    }),

    [dateFilterTypes.WEEK]: Array.from({ length: weekCount }, (_, i) => {
        let today = new Date();

        // Find the most recent Sunday (start of the current week)
        let currentSunday = new Date(today);
        currentSunday.setDate(today.getDate() - today.getDay()); // Move to Sunday
        currentSunday.setHours(0, 0, 0, 0); // Set hours to 00:00

        // Calculate the start and end of the week
        let start = new Date(currentSunday);
        start.setDate(start.getDate() - i * 7); // Move back by 'i' weeks
        start.setHours(0, 0, 0, 0); // Set hours to 00:00

        let end = new Date(start);
        end.setDate(start.getDate() + 6); // Saturday of that week
        end.setHours(23, 59, 59, 999); // Set end hours to 23:59

        const getWeekNumber = (date) => {
            const startOfYear = new Date(date.getFullYear(), 0, 1);
            const pastDaysOfYear = (date - startOfYear) / 86400000;
            return Math.ceil((pastDaysOfYear + startOfYear.getDay() + 1) / 7);
        };

        return {
            id: i + 1, // Unique ID (1 to weekCount)
            name: `Week ${getWeekNumber(start)} (${start.toLocaleDateString()} - ${end.toLocaleDateString()})`, // Format as YYYY-MM-DD
            startDate: start,
            endDate: end,
            weekNumber: `Week ${getWeekNumber(start)}`
        };
    }),

    [dateFilterTypes.MONTH]: Array.from({ length: monthCount }, (_, i) => {
        let end = new Date();
        end.setMonth(end.getMonth() - i + 1, 0); // Last day of the month
        end.setHours(23, 59, 59, 999); // Set end hours to 23:59

        let start = new Date(end.getFullYear(), end.getMonth(), 1); // First day of the month
        start.setHours(0, 0, 0, 0); // Set hours to 00:00

        return {
            id: i + 1, // Unique ID (1 to monthCount)
            name: `${start.toLocaleString('default', { month: 'long' })} ${start.getFullYear()}`, // Name of the month and year
            startDate: start,
            endDate: end
        };
    }),

    [dateFilterTypes.QUARTER]: Array.from({ length: quarterCount }, (_, i) => {
        let now = new Date();
        let currentQuarter = Math.floor(now.getMonth() / 3); // Get the current quarter (0-3)
        let quarterOffset = currentQuarter - i; // Offset from the current quarter
        let year = now.getFullYear();

        // Adjust year if we go back past Q1 of the current year
        if (quarterOffset < 0) {
            year -= 1;
            quarterOffset += 4;
        }

        let startMonth = quarterOffset * 3; // Start month of the quarter (0-indexed)
        let start = new Date(year, startMonth, 1); // First day of the quarter
        start.setHours(0, 0, 0, 0); // Set hours to 00:00

        let end = new Date(year, startMonth + 3, 0); // Last day of the quarter
        end.setHours(23, 59, 59, 999); // Set end hours to 23:59

        return {
            id: i + 1, // Unique ID
            name: `Q${quarterOffset + 1} ${year}`, // Name of the quarter
            startDate: start,
            endDate: end
        };
    }),
    [dateFilterTypes.YEAR]: Array.from({ length: yearCount }, (_, i) => {
        let year = new Date().getFullYear() - i; // Get the current and past year

        let start = new Date(year, 0, 1); // First day of the year (YYYY-01-01)
        start.setHours(0, 0, 0, 0); // Set hours to 00:00

        let end = new Date(year, 11, 31); // Last day of the year (YYYY-12-31)
        end.setHours(23, 59, 59, 999); // Set end hours to 23:59

        return {
            id: i + 1, // Unique ID 
            name: year, // Name of the year
            startDate: start,
            endDate: end
        };
    }),
    [dateFilterTypes.CUSTOM]: Array.from({ length: 1 }, (_, i) => {
        //Current month
        const startDate = new Date(new Date().getFullYear(), new Date().getMonth(), 1);
        const endDate = new Date();

        return {
            id: 1,
            startDate: startDate,
            endDate: endDate,
            name: 'Custom Range'
        };
    })
});

export const onTimeGroups = {
    ON_TIME: 'onTime',
    CLIENT_DELAY: 'clientDelays',
    TENANT_DELAY: 'tenantDelays',
    OTHER_DELAY: 'otherDelays',
    UNSPECIFIED_DELAY: 'unspecifiedDelays'
}

export const onTimePerformancePercentages = [
    { id: onTimeGroups.ON_TIME, name: 'On Time', color: 'rgb(3, 171, 73)', listName: 'onTimeList' },
    { id: onTimeGroups.TENANT_DELAY, name: <TenantNameResolver endText='Delay' />, color: 'red', listName: 'tenantDelaysList' },
    { id: onTimeGroups.OTHER_DELAY, name: 'Other Delay', color: 'rgb(255, 132, 0)', listName: 'otherDelaysList' },
    { id: onTimeGroups.CLIENT_DELAY, name: 'Client Delay', color: 'rgb(247, 205, 65)', listName: 'clientDelaysList' },
    { id: onTimeGroups.UNSPECIFIED_DELAY, name: 'Unspecified Delay', color: '#ac7dda', listName: 'unspecifiedDelaysList' }
]

export const onTimePerformancePercentagesObj = arrayToObject(onTimePerformancePercentages)

export const loadDistanceList = [
    { id: 1, name: '0 - 250' },
    { id: 2, name: '251 - 500' },
    { id: 3, name: '501 - 750' },
    { id: 4, name: '751 - 1000' },
    { id: 5, name: '1001 - 1250' },
    { id: 6, name: '1251 <' }
]